/**
 * DMT Hyperspace Visualizer - Psychedelic breakthrough tunnel with chrysanthemums
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class DMTVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;
  // Adaptive quality controls
  private dynamicQuality: number = 1.0;
  private emaFrameMs: number = 16.7;
  private lastRenderTime?: number;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'dmt',
    name: 'DMT Hyperspace',
    description: 'Intense psychedelic chrysanthemum breakthrough tunnel',
    category: 'artistic',
    defaultZoom: 1.0,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const {
      ctx, width, height, activeWaves, speed, time, zoom, pan,
      smoothedMusicData, isUploadedAudioPlaying, uploadedAudioData
    } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Ultra-intense fractal background with rapid color cycling
    const hueShift = (time * 120) % 360;
    
    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }
    
    // Calculate energy and frequency
    const totalEnergy = smoothedMusicData 
      ? smoothedMusicData.totalEnergy / 10
      : displayWaves.reduce((sum, w) => sum + w.amp, 0);
    const avgFreq = smoothedMusicData
      ? (smoothedMusicData.bassEnergy * 0.3 + smoothedMusicData.midEnergy * 1.5 + smoothedMusicData.trebleEnergy * 3) / (smoothedMusicData.totalEnergy || 1)
      : (displayWaves.length > 0 ? displayWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);
    
    const intensity = Math.min(1, totalEnergy / 150); // Trip intensity - more sensitive
    const hyperSpeed = avgFreq * speed * 5; // Ultra-fast morphing

    // Base quality from canvas size and DPR
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
    const area = width * height * dpr;
    const baseQuality = Math.max(0.5, Math.min(1, 900000 / Math.max(1, area))); // 0.5..1

    // Adaptive quality: EMA of frame time, target ~60 FPS
    if (this.lastRenderTime !== undefined && isFinite(time)) {
      const dt = Math.max(0, (time - this.lastRenderTime));
      const frameMs = Math.min(100, Math.max(1, dt * 1000));
      const alpha = 0.15; // EMA smoothing
      this.emaFrameMs = this.emaFrameMs * (1 - alpha) + frameMs * alpha;
      const targetMs = 16.7;
      const tolerance = 2.5;
      if (this.emaFrameMs > targetMs + tolerance) {
        this.dynamicQuality = Math.max(0.35, this.dynamicQuality - 0.05);
      } else if (this.emaFrameMs < targetMs - tolerance) {
        this.dynamicQuality = Math.min(1.0, this.dynamicQuality + 0.02);
      }
    }
    this.lastRenderTime = time;

    // Combined quality
    const quality = Math.max(0.35, Math.min(1, baseQuality * this.dynamicQuality));
    
    // Separate frequency band reactivity
    const bassReact = smoothedMusicData ? smoothedMusicData.bassEnergy / 50 : intensity * 0.3;
    const midReact = smoothedMusicData ? smoothedMusicData.midEnergy / 50 : intensity * 0.4;
    const trebleReact = smoothedMusicData ? smoothedMusicData.trebleEnergy / 50 : intensity * 0.3;
    const beatPulse = Math.min(1, bassReact * 2); // Strong beat response
    
    // Draw multiple overlapping radial gradients for depth (scaled by quality)
    const bgLayers = quality < 0.8 ? 1 : 2;
    for (let layer = 0; layer < bgLayers; layer++) {
      const layerTime = time * (1 + layer * 0.3);
      const layerHue = (hueShift + layer * 72 + bassReact * 50) % 360; // Bass shifts hue
      const layerScale = (0.65 + layer * 0.12) * (1 + beatPulse * 0.15); // Beat pulse
      const bgGradient = ctx.createRadialGradient(
        cx + Math.cos(layerTime) * (40 + bassReact * 20), 
        cy + Math.sin(layerTime) * (40 + bassReact * 20), 
        0, 
        cx, cy, 
        Math.max(width, height) * layerScale
      );
      bgGradient.addColorStop(0, `hsla(${layerHue}, 100%, 12%, ${0.25})`);
      bgGradient.addColorStop(0.4, `hsla(${(layerHue + 90) % 360}, 100%, 8%, ${0.18})`);
      bgGradient.addColorStop(1, `hsla(${(layerHue + 180) % 360}, 100%, 3%, ${0.1})`);
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
    }
    
    // === FRACTAL MANDALA BACKGROUND ===
    ctx.save();
    ctx.translate(cx + pan.x, cy + pan.y);
    
    // Draw fractal mandala patterns
    const fractalLayers = Math.max(1, Math.round(4 * quality));
    for (let fractalLayer = 0; fractalLayer < fractalLayers; fractalLayer++) {
      const fractalScale = (0.2 + fractalLayer * 0.2) * (1 + trebleReact * 0.4); // Treble expands
      const fractalRotation = time * hyperSpeed * 0.3 * (fractalLayer % 2 === 0 ? 1 : -1) + fractalLayer * 0.5 + midReact;
      const numArms = 6 + fractalLayer * 3 + Math.floor(trebleReact * 4); // More arms with treble
      
      for (let arm = 0; arm < numArms; arm++) {
        const armAngle = (arm / numArms) * Math.PI * 2 + fractalRotation;
        const armHue = (hueShift + fractalLayer * 45 + arm * (360 / numArms) + midReact * 30) % 360;
        
        ctx.save();
        ctx.rotate(armAngle);
        ctx.strokeStyle = `hsla(${armHue}, 100%, ${50 + fractalLayer * 3 + midReact * 20}%, ${0.3 - fractalLayer * 0.03 + midReact * 0.3})`;
        ctx.lineWidth = 2 + midReact;
        ctx.shadowBlur = (10 + midReact * 15) * quality;
        ctx.shadowColor = `hsla(${armHue}, 100%, 70%, 0.5)`;
        
        ctx.beginPath();
        ctx.moveTo(0, 0);
        const radius = Math.min(width, height) * fractalScale * zoom;
        const armPoints = Math.max(8, Math.round(12 * quality));
        for (let i = 0; i <= armPoints; i++) {
          const t = i / armPoints;
          const x = t * radius;
          const y = Math.sin(t * Math.PI * 4 + time * hyperSpeed * 2) * 30 * fractalScale;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.restore();
      }
    }
    
    ctx.restore();
    
    // === CHRYSANTHEMUM BREAKTHROUGH TUNNEL ===
    ctx.save();
    ctx.translate(cx + pan.x, cy + pan.y);
    
    const numPetals = Math.max(6, Math.round((8 + intensity * 8) * quality)); // scaled by quality
    const tunnelDepth = Math.max(8, Math.round(12 * quality)); // scaled by quality
    
    ctx.shadowBlur = 20 * quality;
    
    for (let depth = tunnelDepth; depth >= 0; depth--) {
      const depthRatio = depth / tunnelDepth;
      const scale = Math.pow(depthRatio, 2) * zoom * (1 + beatPulse * 0.15); // Beat expansion
      const layerRadius = (Math.min(width, height) * 0.45) * scale;
      const layerRotation = time * hyperSpeed * (0.8 + depth * 0.05 + bassReact * 0.5) + depth * 0.3; // Bass drives rotation
      const layerHue = (hueShift + depth * 25 + time * 150 + bassReact * 60) % 360; // Bass shifts color
      
      // Draw chrysanthemum petals for this layer with fractals
      for (let petal = 0; petal < numPetals; petal++) {
        const petalAngle = (petal / numPetals) * Math.PI * 2 + layerRotation;
        const petalHue = (layerHue + petal * 15 + depth * 10) % 360;
        
        // Main petal body
        ctx.save();
        ctx.rotate(petalAngle);
        
        const petalLength = layerRadius * (0.8 + midReact * 0.2); // Mid expands petals
        const petalWidth = layerRadius * (0.2 + trebleReact * 0.1); // Treble widens
        const petalMorph = Math.sin(time * hyperSpeed * 3 + petal * 0.5 + depth * 0.3) * (0.3 + beatPulse * 0.3); // Beat morphing
        
        const petalGradient = ctx.createLinearGradient(0, 0, 0, petalLength);
        const brightnessBoost = intensity * 10; // Music intensity boosts brightness
        petalGradient.addColorStop(0, `hsla(${petalHue}, 100%, ${Math.min(95, 80 + brightnessBoost)}%, ${0.9 * (1 - depthRatio)})`);
        petalGradient.addColorStop(0.3, `hsla(${(petalHue + 60) % 360}, 100%, ${Math.min(85, 70 + brightnessBoost)}%, ${0.7 * (1 - depthRatio)})`);
        petalGradient.addColorStop(0.6, `hsla(${(petalHue + 120) % 360}, 100%, ${Math.min(75, 60 + brightnessBoost)}%, ${0.5 * (1 - depthRatio)})`);
        petalGradient.addColorStop(1, `hsla(${(petalHue + 180) % 360}, 100%, ${Math.min(65, 50 + brightnessBoost)}%, ${0.1 * (1 - depthRatio)})`);
        
        ctx.fillStyle = petalGradient;
        ctx.strokeStyle = `hsla(${(petalHue + 90) % 360}, 100%, 90%, ${(0.7 + beatPulse * 0.3) * (1 - depthRatio)})`;
        ctx.lineWidth = 2 + beatPulse;
        
        ctx.shadowBlur = (25 + intensity * 20) * (1 - depthRatio) * quality;
        ctx.shadowColor = `hsla(${petalHue}, 100%, 80%, ${0.8 * (1 - depthRatio)})`;
        
        // Draw morphing petal shape with fractal edges
        ctx.beginPath();
        const petalPoints = Math.max(8, Math.round(12 * quality));
        for (let i = 0; i <= petalPoints; i++) {
          const t = i / petalPoints;
          const angle = t * Math.PI * 2;
          const baseRadius = t < 0.5 ? petalWidth * (t * 2) : petalWidth * (2 - t * 2);
          const fractalOffset = Math.sin(angle * 8 + time * hyperSpeed * 5) * baseRadius * 0.3;
          const x = Math.sin(angle) * (baseRadius + fractalOffset + petalMorph * baseRadius);
          const y = t * petalLength;
          
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Add glowing veins to petals (only on near layers)
        if (depthRatio < 0.5) {
          ctx.strokeStyle = `hsla(${(petalHue + 180) % 360}, 100%, 90%, ${0.6 * (1 - depthRatio)})`;
          ctx.lineWidth = 1;
          ctx.shadowBlur = 15 * quality;
          for (let vein = 0; vein < 2; vein++) {
            ctx.beginPath();
            ctx.moveTo(0, 0);
            const veinAngle = (vein - 0.5) * 0.3;
            ctx.lineTo(
              Math.sin(veinAngle) * petalWidth * 0.5,
              petalLength
            );
            ctx.stroke();
          }
        }
        
        ctx.restore();
      }
      
      // Pulsating central jewel for each layer with sacred geometry
      const orbSize = layerRadius * (0.25 + beatPulse * 0.15); // Bass expands orbs
      const orbPulse = 1 + Math.sin(time * hyperSpeed * 4 + depth * 0.5) * 0.4 + beatPulse * 0.3;
      
      // Draw sacred geometry in the orb
      const geometryPoints = 6;
      for (let geo = 0; geo < 3; geo++) {
        const geoSize = orbSize * orbPulse * (1 - geo * 0.25);
        const geoRotation = time * hyperSpeed * (geo + 1) * (geo % 2 === 0 ? 1 : -1);
        
        ctx.strokeStyle = `hsla(${(layerHue + geo * 120) % 360}, 100%, ${80 - geo * 10}%, ${(0.7 - geo * 0.2) * (1 - depthRatio)})`;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 20 * quality;
        ctx.shadowColor = `hsla(${(layerHue + geo * 120) % 360}, 100%, 80%, ${0.8 * (1 - depthRatio)})`;
        
        ctx.beginPath();
        for (let p = 0; p <= geometryPoints; p++) {
          const angle = (p / geometryPoints) * Math.PI * 2 + geoRotation;
          const x = Math.cos(angle) * geoSize;
          const y = Math.sin(angle) * geoSize;
          if (p === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
      }
      
      const orbGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, orbSize * orbPulse);
      orbGradient.addColorStop(0, `hsla(${(layerHue + 180) % 360}, 100%, 95%, ${0.95 * (1 - depthRatio)})`);
      orbGradient.addColorStop(0.3, `hsla(${(layerHue + 120) % 360}, 100%, 80%, ${0.7 * (1 - depthRatio)})`);
      orbGradient.addColorStop(0.6, `hsla(${layerHue}, 100%, 60%, ${0.4 * (1 - depthRatio)})`);
      orbGradient.addColorStop(1, `hsla(${(layerHue + 240) % 360}, 100%, 40%, 0)`);
      
      ctx.fillStyle = orbGradient;
      ctx.shadowBlur = 30 * (1 - depthRatio);
      ctx.shadowColor = `hsla(${layerHue}, 100%, 90%, ${0.9 * (1 - depthRatio)})`;
      ctx.beginPath();
      ctx.arc(0, 0, orbSize * orbPulse, 0, Math.PI * 2);
      ctx.fill();
    }
    
    ctx.restore();
    
    // === KALEIDOSCOPIC ENTITY SWARMS ===
    const numEntityRings = Math.max(2, Math.round(3 * quality));
    
    for (let ring = 0; ring < numEntityRings; ring++) {
      const numEntities = Math.max(6, Math.round((10 + ring * 6) * quality)) + Math.floor(intensity * 6); // scaled by quality
      const ringRadius = (Math.min(width, height) * (0.15 + ring * 0.1)) * (1 + midReact * 0.3);
      const ringRotation = time * hyperSpeed * (0.5 + ring * 0.3 + trebleReact * 0.4) * (ring % 2 === 0 ? 1 : -1); // Treble speeds rotation
      
      for (let i = 0; i < numEntities; i++) {
        const entityAngle = (i / numEntities) * Math.PI * 2 + ringRotation;
        const radiusModulation = Math.sin(time * 3 + i * 0.5 + ring) * 30;
        
        const ex = (cx + pan.x) + Math.cos(entityAngle) * (ringRadius + radiusModulation);
        const ey = (cy + pan.y) + Math.sin(entityAngle) * (ringRadius + radiusModulation);
        
        const entityHue = (hueShift + i * (360 / numEntities) + ring * 60 + time * 200 + trebleReact * 80) % 360;
        const size = (15 + Math.sin(time * 8 + i * 0.3 + ring) * 8) * (1 + beatPulse * 0.5); // Beat expands
        const rotation = time * hyperSpeed * (3 + midReact) + i;
        
        ctx.save();
        ctx.translate(ex, ey);
        ctx.rotate(rotation);
        
        // Multi-layered entity with trails
        for (let layer = 1; layer >= 0; layer--) {
          const layerScale = 1 - layer * 0.25;
          const layerAlpha = (1 - layer * 0.3) * 0.8;
          
          const entityGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, size * layerScale);
          entityGradient.addColorStop(0, `hsla(${entityHue}, 100%, ${Math.min(100, 95 + intensity * 5)}%, ${layerAlpha})`);
          entityGradient.addColorStop(0.5, `hsla(${(entityHue + 80) % 360}, 100%, ${Math.min(90, 75 + intensity * 10)}%, ${layerAlpha * 0.7})`);
          entityGradient.addColorStop(1, `hsla(${(entityHue + 160) % 360}, 100%, 55%, 0)`);
          
          ctx.fillStyle = entityGradient;
          ctx.strokeStyle = `hsla(${(entityHue + layer * 60) % 360}, 100%, 90%, ${layerAlpha * (1 + beatPulse * 0.3)})`;
          ctx.lineWidth = 2 + beatPulse;
          ctx.shadowBlur = (20 + intensity * 15) * quality;
          ctx.shadowColor = `hsla(${entityHue}, 100%, 80%, ${layerAlpha})`;
          
          // Complex morphing geometry
          const shapeType = (i + ring) % 5;
          const numSides = [3, 4, 5, 6, 8][shapeType];
          
          ctx.beginPath();
          for (let j = 0; j <= numSides; j++) {
            const angle = (j / numSides) * Math.PI * 2;
            const radiusWarp = 1 + Math.sin(angle * 3 + time * hyperSpeed * 5) * 0.3;
            const px = Math.cos(angle) * size * layerScale * radiusWarp;
            const py = Math.sin(angle) * size * layerScale * radiusWarp;
            if (j === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          
          // Add inner star pattern
          if (layer === 0) {
            ctx.strokeStyle = `hsla(${(entityHue + 180) % 360}, 100%, 100%, ${layerAlpha})`;
            ctx.lineWidth = 1;
            for (let star = 0; star < numSides; star++) {
              const angle = (star / numSides) * Math.PI * 2;
              ctx.beginPath();
              ctx.moveTo(0, 0);
              ctx.lineTo(Math.cos(angle) * size * 0.4, Math.sin(angle) * size * 0.4);
              ctx.stroke();
            }
          }
        }
        
        ctx.restore();
      }
    }
    
    // === SPIRALING ENERGY VORTEXES ===
    const numVortexes = Math.max(3, Math.round(5 * quality));
    
    for (let v = 0; v < numVortexes; v++) {
      const vortexAngle = (v / numVortexes) * Math.PI * 2 + time * hyperSpeed * (0.4 + bassReact * 0.3);
      const vortexDistance = Math.min(width, height) * (0.35 + beatPulse * 0.1);
      const vortexX = (cx + pan.x) + Math.cos(vortexAngle) * vortexDistance;
      const vortexY = (cy + pan.y) + Math.sin(vortexAngle) * vortexDistance;
      
      const vortexHue = (hueShift + v * 45 + time * 100 + bassReact * 50) % 360;
      
      // Draw spiral trails
      const numSpiralArms = 3;
      for (let arm = 0; arm < numSpiralArms; arm++) {
        ctx.beginPath();
        const armOffset = (arm / numSpiralArms) * Math.PI * 2;
        
        const spiralPoints = Math.max(18, Math.round(30 * quality));
        for (let i = 0; i < spiralPoints; i++) {
          const t = i / spiralPoints;
          const spiralAngle = armOffset + t * Math.PI * 4 + time * hyperSpeed * 2;
          const spiralRadius = t * 80 * (1 + Math.sin(time * 5 + v) * 0.3);
          
          const sx = vortexX + Math.cos(spiralAngle) * spiralRadius;
          const sy = vortexY + Math.sin(spiralAngle) * spiralRadius;
          
          if (i === 0) ctx.moveTo(sx, sy);
          else ctx.lineTo(sx, sy);
        }
        
        const armHue = (vortexHue + arm * 120) % 360;
        ctx.strokeStyle = `hsla(${armHue}, 100%, ${70 + midReact * 15}%, ${0.6 + midReact * 0.3})`;
        ctx.lineWidth = 3 + midReact * 2;
        ctx.shadowBlur = (25 + midReact * 20) * quality;
        ctx.shadowColor = `hsla(${armHue}, 100%, 80%, ${0.8 + midReact * 0.2})`;  
        ctx.stroke();
      }
    }
    
    // === PLASMA WISPS WITH TRAILS ===
    const numWisps = Math.max(12, Math.round((20 + intensity * 25) * quality));
    
    for (let i = 0; i < numWisps; i++) {
      const wispAngle = (i / numWisps) * Math.PI * 2 + time * hyperSpeed * (0.6 + trebleReact * 0.5); // Treble speeds wisps
      const wispRadius = (Math.min(width, height) * 0.3) + Math.sin(time * 3 + i * 0.5) * (120 + trebleReact * 50);
      
      const wx = (cx + pan.x) + Math.cos(wispAngle) * wispRadius;
      const wy = (cy + pan.y) + Math.sin(wispAngle) * wispRadius;
      
      const wispHue = (hueShift + i * 8 + time * 180 + trebleReact * 60) % 360;
      const wispSize = (12 + Math.sin(time * 6 + i * 0.3) * 8) * (1 + beatPulse * 0.4); // Beat expands
      
      // Draw trailing effect
      for (let trail = 2; trail >= 0; trail--) {
        const trailAngle = wispAngle - trail * 0.1;
        const trailRadius = wispRadius - trail * 15;
        const trailAlpha = (1 - trail * 0.25) * 0.7;
        
        const tx = (cx + pan.x) + Math.cos(trailAngle) * trailRadius;
        const ty = (cy + pan.y) + Math.sin(trailAngle) * trailRadius;
        
        const trailGradient = ctx.createRadialGradient(tx, ty, 0, tx, ty, wispSize * (1 - trail * 0.2));
        trailGradient.addColorStop(0, `hsla(${wispHue}, 100%, ${Math.min(100, 95 + intensity * 5)}%, ${trailAlpha * (1 + beatPulse * 0.3)})`);
        trailGradient.addColorStop(0.4, `hsla(${(wispHue + 60) % 360}, 100%, ${Math.min(90, 75 + intensity * 10)}%, ${trailAlpha * 0.6})`);
        trailGradient.addColorStop(1, `hsla(${(wispHue + 120) % 360}, 100%, 55%, 0)`);
        
        ctx.fillStyle = trailGradient;
        ctx.shadowBlur = (25 + intensity * 20) * quality;
        ctx.shadowColor = `hsla(${wispHue}, 100%, 80%, ${trailAlpha * (1 + beatPulse * 0.3)})`;  
        
        ctx.beginPath();
        ctx.arc(tx, ty, wispSize * (1 - trail * 0.2), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // === INTERDIMENSIONAL PORTAL (CENTER) ===
    ctx.save();
    ctx.translate(cx + pan.x, cy + pan.y);
    
    const portalPulse = 1 + Math.sin(time * hyperSpeed * 3) * 0.5 + beatPulse * 0.5; // Strong beat pulse
    const portalSize = 50 * portalPulse * zoom * (0.5 + intensity * 1.5); // Much bigger with music
    
    // Draw portal rings
    for (let ring = 6; ring >= 0; ring--) {
      const ringSize = portalSize * (ring / 6) * (1 + beatPulse * 0.2);
      const ringRotation = time * hyperSpeed * (ring * 0.3 + bassReact * 0.5) * (ring % 2 === 0 ? 1 : -1); // Bass speeds rotation
      const ringHue = (hueShift + ring * 36 + bassReact * 40) % 360; // Bass shifts hue
      
      // Outer glow
      const glowGradient = ctx.createRadialGradient(0, 0, ringSize * 0.8, 0, 0, ringSize);
      glowGradient.addColorStop(0, `hsla(${ringHue}, 100%, 60%, 0)`);
      glowGradient.addColorStop(0.7, `hsla(${ringHue}, 100%, 70%, ${0.3})`);
      glowGradient.addColorStop(1, `hsla(${ringHue}, 100%, 80%, ${0.6})`);
      
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(0, 0, ringSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Geometric ring pattern
      const numSegments = Math.max(8, Math.round((10 + ring * 2) * quality));
      ctx.save();
      ctx.rotate(ringRotation);
      
      for (let seg = 0; seg < numSegments; seg++) {
        const segAngle = (seg / numSegments) * Math.PI * 2;
        const segHue = (ringHue + seg * (360 / numSegments)) % 360;
        
        ctx.strokeStyle = `hsla(${segHue}, 100%, 90%, ${0.8})`;
        ctx.lineWidth = 3;
        ctx.shadowBlur = 15 * quality;
        ctx.shadowColor = `hsla(${segHue}, 100%, 90%, 0.9)`;
        
        ctx.beginPath();
        ctx.arc(0, 0, ringSize, segAngle, segAngle + (Math.PI * 2 / numSegments) * 0.7);
        ctx.stroke();
      }
      
      ctx.restore();
    }
    
    // Central white-hot core
    const coreGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, portalSize * 0.3);
    coreGradient.addColorStop(0, `hsla(${(hueShift + 180) % 360}, 100%, 100%, ${1})`);
    coreGradient.addColorStop(0.2, `hsla(${hueShift}, 100%, 95%, ${1})`);
    coreGradient.addColorStop(0.5, `hsla(${(hueShift + 90) % 360}, 100%, ${Math.min(95, 80 + intensity * 15)}%, 0.9)`);
    coreGradient.addColorStop(0.8, `hsla(${(hueShift + 180) % 360}, 100%, ${Math.min(80, 60 + intensity * 20)}%, 0.5)`);
    coreGradient.addColorStop(1, `hsla(${(hueShift + 270) % 360}, 100%, 40%, 0)`);
    
    ctx.fillStyle = coreGradient;
    ctx.shadowBlur = (60 + beatPulse * 40) * quality; // Dramatic beat glow
    ctx.shadowColor = `hsla(${hueShift}, 100%, 90%, ${1 + beatPulse * 0.5})`;
    ctx.beginPath();
    ctx.arc(0, 0, portalSize * 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Rotating sacred geometry overlay
    const geomSides = 8;
    const geomRotation = time * hyperSpeed * (2 + trebleReact); // Treble speeds geometry
    
    for (let layer = 0; layer < 3; layer++) {
      ctx.save();
      ctx.rotate(geomRotation * (layer % 2 === 0 ? 1 : -1));
      
      ctx.strokeStyle = `hsla(${(hueShift + layer * 120) % 360}, 100%, 95%, ${0.7 - layer * 0.2})`;
      ctx.lineWidth = 2;
      ctx.shadowBlur = 20 * quality;
      
      const geomSize = portalSize * 0.25 * (1 + layer * 0.3);
      ctx.beginPath();
      for (let i = 0; i <= geomSides; i++) {
        const angle = (i / geomSides) * Math.PI * 2;
        const x = Math.cos(angle) * geomSize;
        const y = Math.sin(angle) * geomSize;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      
      // Draw connecting lines (flower of life pattern)
      for (let i = 0; i < geomSides; i++) {
        const angle = (i / geomSides) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(angle) * geomSize, Math.sin(angle) * geomSize);
        ctx.stroke();
      }
      
      ctx.restore();
    }
    
    ctx.restore();
    ctx.shadowBlur = 0;
  }
}
